// src/features/auth/pages/ChangeUserPage/componenets/SetPinDialog.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Dialpad, Lock } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PinKeypad from '../../../../../components/common/PinKeypad';

/**
 * Self-service set/change/remove a PIN from the Profile page. Always asks for
 * the current password (no fresh password in memory here, unlike the
 * first-time setup flow which lives at /force-pin instead of a dialog).
 * - mode="profile": set or change the PIN.
 * - mode="disable": remove the PIN — only the password field is shown.
 */
export default function SetPinDialog({
  open,
  mode = 'profile',
  onConfirm,
  onClose,
  loading = false,
  serverError = null,
  onClearError = null,
}) {
  const isDisableMode = mode === 'disable';
  const [pin, setPin] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const { t } = useTranslation(['auth', 'common', 'staff']);
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin('');
      setCurrentPassword('');
      setValidationError('');
      // MUI Dialog mounts on a delay (transition) — plain autoFocus on the
      // TextField is unreliable there, so focus explicitly once it's in.
      const timeoutId = setTimeout(() => passwordInputRef.current?.focus(), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  const handleConfirm = () => {
    if (loading) return;
    if (!currentPassword.trim()) {
      setValidationError(t('common:messages.password_required'));
      return;
    }
    if (!isDisableMode && !/^\d{4}$/.test(pin)) {
      setValidationError(t('auth:password_dialog.pin_required'));
      return;
    }

    setValidationError('');
    onConfirm({ pin, currentPassword });
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Dialpad color="primary" />
          <Typography variant="h6">
            {isDisableMode ? t('staff:profile.pin_disable_confirm_title') : t('auth:pin_setup.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          {isDisableMode ? t('staff:profile.pin_disable_confirm_message') : t('auth:pin_setup.description')}
        </Typography>

        {serverError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {serverError}
          </Alert>
        )}

        <TextField
          fullWidth
          inputRef={passwordInputRef}
          type="password"
          autoComplete="current-password"
          label={t('common:labels.password')}
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            if (validationError) setValidationError('');
            if (serverError && onClearError) onClearError();
          }}
          onKeyPress={handleKeyPress}
          error={isDisableMode && !!validationError}
          helperText={isDisableMode ? validationError : ''}
          disabled={loading}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        {!isDisableMode && (
          <Box sx={{ py: 2 }}>
            <PinKeypad
              value={pin}
              onChange={setPin}
              disabled={loading}
              error={!!validationError}
              onComplete={handleConfirm}
              onEnter={handleConfirm}
            />
            {validationError && (
              <Typography variant="body2" color="error" textAlign="center" mt={2}>
                {validationError}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isDisableMode ? 'error' : 'primary'}
          disabled={loading || !currentPassword.trim() || (!isDisableMode && pin.length !== 4)}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {isDisableMode ? t('staff:profile.disable_pin') : t('auth:pin_setup.set_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
